/**
 * MCP API Client
 */

import { api } from '@/lib/api-client';
import type {
  ToolListResponse,
  ToolSearchResponse,
  ToolDetailResponse,
  ToolInvocationResponse,
  MCPStatsResponse,
  CategoriesResponse,
  ProvidersResponse,
  TagsResponse,
  ToolListOptions,
} from './types';

const MCP_BASE = '/api/v1/mcp';

/**
 * List all tools with filtering and pagination
 */
export async function listTools(options: ToolListOptions = {}): Promise<ToolListResponse> {
  const params: Record<string, string> = {};

  if (options.source) params.source = options.source;
  if (options.category) params.category = options.category;
  if (options.provider) params.provider = options.provider;
  if (options.popular !== undefined) params.popular = String(options.popular);
  if (options.search) params.search = options.search;
  if (options.tags?.length) params.tags = options.tags.join(',');
  if (options.limit) params.limit = String(options.limit);
  if (options.offset) params.offset = String(options.offset);

  return api.get<ToolListResponse>(`${MCP_BASE}/tools`, { params });
}

/**
 * Search tools by query
 */
export async function searchTools(
  query: string,
  options?: { limit?: number; source?: string }
): Promise<ToolSearchResponse> {
  const params: Record<string, string> = { q: query };
  if (options?.limit) params.limit = String(options.limit);
  if (options?.source) params.source = options.source;

  return api.get<ToolSearchResponse>(`${MCP_BASE}/tools/search`, { params });
}

/**
 * Get popular tools
 */
export async function getPopularTools(limit: number = 20): Promise<ToolSearchResponse> {
  return api.get<ToolSearchResponse>(`${MCP_BASE}/tools/popular`, {
    params: { limit: String(limit) },
  });
}

/**
 * Get tools for a specific provider
 */
export async function getToolsByProvider(provider: string): Promise<ToolSearchResponse> {
  return api.get<ToolSearchResponse>(`${MCP_BASE}/tools/by-provider/${provider}`);
}

/**
 * Get tools for a specific category
 */
export async function getToolsByCategory(category: string): Promise<ToolSearchResponse> {
  return api.get<ToolSearchResponse>(`${MCP_BASE}/tools/by-category/${category}`);
}

/**
 * Get tool details by ID
 */
export async function getTool(toolId: string): Promise<ToolDetailResponse> {
  return api.get<ToolDetailResponse>(`${MCP_BASE}/tools/${encodeURIComponent(toolId)}`);
}

/**
 * Invoke a tool
 */
export async function invokeTool(
  toolId: string,
  params: Record<string, unknown>
): Promise<ToolInvocationResponse> {
  return api.post<ToolInvocationResponse>(
    `${MCP_BASE}/tools/${encodeURIComponent(toolId)}/invoke`,
    params
  );
}

/**
 * Get all categories with counts
 */
export async function getCategories(): Promise<CategoriesResponse> {
  return api.get<CategoriesResponse>(`${MCP_BASE}/categories`);
}

/**
 * Get all providers with counts
 */
export async function getProviders(): Promise<ProvidersResponse> {
  return api.get<ProvidersResponse>(`${MCP_BASE}/providers`);
}

/**
 * Get all available tags
 */
export async function getTags(): Promise<TagsResponse> {
  return api.get<TagsResponse>(`${MCP_BASE}/tags`);
}

/**
 * Get MCP statistics
 */
export async function getStats(): Promise<MCPStatsResponse> {
  return api.get<MCPStatsResponse>(`${MCP_BASE}/stats`);
}

/**
 * Sync tools (admin only)
 */
export async function syncTools(): Promise<MCPStatsResponse> {
  return api.post<MCPStatsResponse>(`${MCP_BASE}/sync-tools`);
}

export default {
  listTools,
  searchTools,
  getPopularTools,
  getToolsByProvider,
  getToolsByCategory,
  getTool,
  invokeTool,
  getCategories,
  getProviders,
  getTags,
  getStats,
  syncTools,
};
